// Admin Invites Service
// Owner-only view of invite codes and code requests (ITER-098). Writes go through callables only.

import { collection, onSnapshot, orderBy, query, Timestamp, Unsubscribe, DocumentData } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';

export type InviteCodeStatus = 'active' | 'used' | 'revoked';
export type InviteCodeRequestStatus = 'pending' | 'handled' | 'declined';
export type RequesterEmailStatus = 'sent' | 'failed' | 'skipped';
export type EmailActionState = 'ok' | 'invalid' | 'used' | 'expired' | 'already-handled';
export type InviteRequestActionKind = 'sendCode' | 'decline';

export interface InviteCode {
  id: string;
  code: string;
  label: string | null;
  status: InviteCodeStatus;
  createdAt: string;
  createdBy: string;
  usedAt: string | null;
  usedByUid: string | null;
  usedByEmail: string | null;
  usedByName: string | null;
  activatedAt: string | null;
  revokedAt: string | null;
}

export interface InviteCodeRequest {
  id: string;
  name: string;
  email: string;
  note: string;
  status: InviteCodeRequestStatus;
  createdAt: string;
  handledAt: string | null;
  handledBy: string | null;
  declinedAt: string | null;
  sentCodeId: string | null;
  requesterEmailStatus: RequesterEmailStatus | null;
  requesterEmailError: string | null;
  receivedEmailSentAt: string | null;
}

export interface InviteRequestActionPreview {
  state: EmailActionState;
  kind?: InviteRequestActionKind;
  requesterName?: string;
  requesterEmail?: string;
  requesterNote?: string;
}

export interface InviteRequestAnswer {
  outcome: 'sent' | 'declined';
  emailStatus: RequesterEmailStatus;
  code?: string;
}

export interface InviteRequestActionResult {
  outcome: 'sent' | 'declined';
  emailStatus: RequesterEmailStatus;
  requesterName: string;
}

function toIsoString(value: unknown): string | null {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  return typeof value === 'string' ? value : null;
}

function toNullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function toRequesterEmailStatus(value: unknown): RequesterEmailStatus | null {
  return value === 'sent' || value === 'failed' || value === 'skipped' ? value : null;
}

export function docToInviteCode(id: string, data: DocumentData): InviteCode {
  return {
    id,
    code: data.code ?? '',
    label: toNullableString(data.label),
    status: data.status ?? 'active',
    createdAt: toIsoString(data.createdAt) ?? '',
    createdBy: data.createdBy ?? '',
    usedAt: toIsoString(data.usedAt),
    usedByUid: toNullableString(data.usedByUid),
    usedByEmail: toNullableString(data.usedByEmail),
    usedByName: toNullableString(data.usedByName),
    activatedAt: toIsoString(data.activatedAt),
    revokedAt: toIsoString(data.revokedAt),
  };
}

export function docToInviteCodeRequest(id: string, data: DocumentData): InviteCodeRequest {
  return {
    id,
    name: data.name ?? '',
    email: data.email ?? '',
    note: data.note ?? '',
    status: data.status ?? 'pending',
    createdAt: toIsoString(data.createdAt) ?? '',
    handledAt: toIsoString(data.handledAt),
    handledBy: toNullableString(data.handledBy),
    declinedAt: toIsoString(data.declinedAt),
    sentCodeId: toNullableString(data.sentCodeId),
    requesterEmailStatus: toRequesterEmailStatus(data.requesterEmailStatus),
    requesterEmailError: toNullableString(data.requesterEmailError),
    receivedEmailSentAt: toIsoString(data.receivedEmailSentAt),
  };
}

export function subscribeToInviteCodes(
  onChange: (codes: InviteCode[]) => void,
  onError: (error: Error) => void
): Unsubscribe {
  const codesQuery = query(collection(db, 'inviteCodes'), orderBy('createdAt', 'desc'));
  return onSnapshot(
    codesQuery,
    (snapshot) => onChange(snapshot.docs.map((codeDoc) => docToInviteCode(codeDoc.id, codeDoc.data()))),
    onError
  );
}

export function subscribeToInviteCodeRequests(
  onChange: (requests: InviteCodeRequest[]) => void,
  onError: (error: Error) => void
): Unsubscribe {
  const requestsQuery = query(collection(db, 'inviteCodeRequests'), orderBy('createdAt', 'desc'));
  return onSnapshot(
    requestsQuery,
    (snapshot) => onChange(snapshot.docs.map((requestDoc) => docToInviteCodeRequest(requestDoc.id, requestDoc.data()))),
    onError
  );
}

export async function createInviteCode(label?: string): Promise<{ id: string; code: string }> {
  const callable = httpsCallable<{ label?: string }, { id: string; code: string }>(functions, 'createInviteCode');
  const result = await callable({ label });
  return result.data;
}

export async function revokeInviteCode(codeId: string): Promise<void> {
  const callable = httpsCallable<{ codeId: string }, { ok: boolean }>(functions, 'revokeInviteCode');
  await callable({ codeId });
}

export async function markInviteCodeRequestHandled(requestId: string): Promise<void> {
  const callable = httpsCallable<{ requestId: string }, { ok: boolean }>(functions, 'markInviteCodeRequestHandled');
  await callable({ requestId });
}

export async function sendInviteCodeForRequest(requestId: string): Promise<InviteRequestAnswer> {
  const callable = httpsCallable<{ requestId: string }, InviteRequestAnswer>(functions, 'sendInviteCodeForRequest');
  const result = await callable({ requestId });
  return result.data;
}

export async function declineInviteCodeRequest(requestId: string): Promise<InviteRequestAnswer> {
  const callable = httpsCallable<{ requestId: string }, InviteRequestAnswer>(functions, 'declineInviteCodeRequest');
  const result = await callable({ requestId });
  return result.data;
}

export async function previewInviteRequestAction(token: string): Promise<InviteRequestActionPreview> {
  const callable = httpsCallable<{ token: string }, InviteRequestActionPreview>(functions, 'previewInviteRequestAction');
  const result = await callable({ token });
  return result.data;
}

export async function confirmInviteRequestAction(token: string): Promise<InviteRequestActionResult> {
  const callable = httpsCallable<{ token: string }, InviteRequestActionResult>(functions, 'confirmInviteRequestAction');
  const result = await callable({ token });
  return result.data;
}

export function formatInviteCode(code: string): string {
  return `${code.slice(0, 4)}-${code.slice(4)}`;
}
